import { GridCenterView, GridSidePaneView } from "../components/gridView";
import React, { useContext, useEffect, useState } from "react";
import {
  selectLocalPeerID,
  selectLocalPeerRole,
  selectPeers,
  selectPeersByRoles,
  selectRolesMap,
  useHMSStore,
} from "@100mslive/react-sdk";

import { Flex } from "@100mslive/react-ui";
import { LiveHodoContext } from "../contexts/LiveHodoContext";
import { NonPublisherView } from "./NonPublisherView";
import { ROLES } from "../common/roles";
import { UI_SETTINGS } from "../common/constants";
import { useAppLayout } from "../components/AppData/useAppLayout";
import { useNavigation } from "../components/hooks/useNavigation";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUISettings } from "../components/AppData/useUISettings";

export const MainGridView = ({ isCabin }) => {
  const {
    stateCtx: { selectedCabinData },
  } = useContext(LiveHodoContext);

  const { t } = useTranslation();
  const navigate = useNavigation();
  const { roomId, role } = useParams();

  const centerRoles = useAppLayout("center") || [];
  const sidepaneRoles = useAppLayout("sidepane") || [];
  const maxTileCount = useUISettings(UI_SETTINGS.maxTileCount);
  const peers = useHMSStore(selectPeers);
  const roles = useHMSStore(selectRolesMap);
  const localPeerId = useHMSStore(selectLocalPeerID);
  const localRole = useHMSStore(selectLocalPeerRole);
  const peersByRoles = useHMSStore(
    selectPeersByRoles(localRole.subscribeParams.subscribeToRoles || [])
  );
  const [placeholder, setPlaceholder] = useState("");
  const [listPeers, setListPeers] = useState([]);
  const centerPeers = listPeers.filter(peer =>
    centerRoles.includes(peer?.roleName)
  );
  const sidebarPeers = listPeers.filter(peer =>
    sidepaneRoles.includes(peer?.roleName)
  );

  useEffect(() => {
    if (isCabin) {
      const listHost = peers?.filter(
        peer =>
          peer.roleName === ROLES.HOST ||
          peer.roleName === ROLES.PRESENTER ||
          (peer.isLocal && peer.roleName === ROLES.GUEST)
      );
      const hasSameData = listHost.find(
        peer => peer?.id === selectedCabinData?.id
      );

      if (!hasSameData) {
        listHost.push(selectedCabinData);
      }
      setListPeers(listHost);
    } else {
      setListPeers(peers);
    }
  }, [isCabin, selectedCabinData, peers]);

  useEffect(() => {
    const hasPublishingPeers = listPeers.some(peer => {
      // peer able to publish
      if (peer?.roleName && roles[peer?.roleName]) {
        return !!roles[peer?.roleName]?.publishParams?.allowed.length;
      }
      return true;
    });
    const hasSubscribedRolePublishing = peersByRoles.some(peer => {
      if (peer?.roleName && roles[peer?.roleName]) {
        return !!roles[peer?.roleName]?.publishParams?.allowed.length;
      }
      return true;
    });
    if (!hasPublishingPeers) {
      setPlaceholder(t("lbl_curentky_there_is_no_one_else_in_the_room"));
    } else if (!localRole.subscribeParams.subscribeToRoles?.length) {
      setPlaceholder("lbl_this_role_does_not_exist");
    } else if (!hasSubscribedRolePublishing) {
      if (localRole?.name === ROLES.GUEST) {
        let selectCabinULR = `/select/${roomId}`;
        if (role) {
          selectCabinULR += `/${role}`;
        }
        navigate(selectCabinULR);
        return;
      }
      setPlaceholder("lbl_this_role_has_not_been_published");
    } else {
      setPlaceholder("");
    }
  }, [
    isCabin,
    localRole.subscribeParams.subscribeToRoles?.length,
    listPeers,
    peersByRoles,
    roles,
  ]);

  /**
   * If there are peers from many publishing roles, then it's possible to divide
   * them into two parts, those who show in center and those who show in sidepane.
   * In case there is only one person in the room, then too sidepane will be shown
   * and center would be taken up by a banner image.
   * There is an issue currently, where the banner is still shown if there are
   * multiple viewers in the room but no publisher. Depending on the use case
   * this can be useful(for webinar) or look odd(for showing you're the only one).
   * Note that both center peers and sidebar peers have only publishing peers in them.
   */
  let showSidePane = centerPeers.length > 0 && sidebarPeers.length > 0;
  if (centerPeers.length === 0) {
    // we'll show the sidepane for banner in this case too if 1). it's only me
    // in the room. or 2). noone is publishing in the room
    const itsOnlyMeInTheRoom =
      listPeers.length === 1 && listPeers[0]?.id === localPeerId;
    const nooneIsPublishing = sidebarPeers.length === 0;
    showSidePane = itsOnlyMeInTheRoom || nooneIsPublishing;
  }

  return (
    <Flex
      css={{
        size: "100%",
      }}
      direction={{
        "@initial": "row",
        "@md": "column",
      }}
    >
      {placeholder ? (
        <NonPublisherView message={placeholder} />
      ) : (
        <>
          <GridCenterView
            peers={showSidePane ? centerPeers : listPeers}
            maxTileCount={maxTileCount}
            allowRemoteMute={false}
            hideSidePane={!showSidePane}
            totalPeers={listPeers.length}
          />
          {showSidePane && (
            <GridSidePaneView
              peers={sidebarPeers}
              totalPeers={listPeers.length}
            />
          )}
        </>
      )}
    </Flex>
  );
};
